import React, { useEffect, useState, useMemo } from 'react';
import { Button } from '@/components/ui/Button';
import { Input, Select, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { api } from '@/api/client';
import { INVOICE_STATUSES, formatNumber } from '@/utils/helpers';
import { useCurrency, formatCurrency } from '@/hooks/useCurrency';
import { Plus, Trash, Search, Sparkles, Barcode, ListChecks } from 'lucide-react';
import { ProductPicker } from '@/components/ui/ProductPicker';
import { SerialSelectorModal } from '@/components/ui/SerialSelectorModal';

const DEFAULT_PAYMENT_METHODS = ['Cash', 'Card', 'Bank Transfer', 'Cheque', 'Online', 'Koko', 'Mintpay', 'PayHere'];

const emptyItem = () => ({
  product_id: '',
  product_name: '',
  serial: '',
  qty: 1,
  price: 0
});

export const InvoiceForm = ({ isOpen, onClose, invoice = null, onSaved }) => {
  const currency = useCurrency();
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState(DEFAULT_PAYMENT_METHODS);
  const [loading, setLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [showNewCustomer, setShowNewCustomer] = useState(false);
  const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '' });
  const [creditBalance, setCreditBalance] = useState(null); // { outstanding, credits: [...] }
  const [applyCredit, setApplyCredit] = useState(false);
  const [creditApplied, setCreditApplied] = useState(0);
  const [quickSerial, setQuickSerial] = useState('');
  const [quickAddMsg, setQuickAddMsg] = useState('');
  const [bulkSerialState, setBulkSerialState] = useState(null); // { rowIndex, product, defaultQty }

  const [formData, setFormData] = useState({
    customer_id: '',
    items: [emptyItem()],
    discount: 0,
    paid_amount: 0,
    payment_method: 'Cash',
    payment_type: 'Cash',
    credit_days: 30,
    due_date: '',
    status: 'Draft',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      loadData();
      if (invoice) {
        setFormData({
          customer_id: invoice.customer_id,
          items: invoice.items?.length ? invoice.items : [emptyItem()],
          discount: invoice.discount || 0,
          paid_amount: invoice.paid_amount || 0,
          payment_method: invoice.payment_method || 'Cash',
          payment_type: invoice.payment_type || invoice.payment_method || 'Cash',
          credit_days: invoice.credit_days || 30,
          due_date: invoice.due_date || '',
          status: invoice.status || 'Draft',
          notes: invoice.notes || ''
        });
      } else {
        setFormData({
          customer_id: '',
          items: [emptyItem()],
          discount: 0,
          paid_amount: 0,
          payment_method: 'Cash',
          payment_type: 'Cash',
          credit_days: 30,
          due_date: '',
          status: 'Draft',
          notes: ''
        });
      }
    }
  }, [isOpen, invoice]);

  const loadData = async () => {
    try {
      const [custRes, prodRes, settingsRes] = await Promise.all([
        api.getCustomers(),
        api.getProducts(),
        api.getSettings().catch(() => ({ data: null }))
      ]);
      setCustomers(custRes.data);
      setProducts(prodRes.data);
      // Parse payment methods from settings
      if (settingsRes.data?.payment_methods) {
        const methods = settingsRes.data.payment_methods
          .split(',')
          .map(m => m.trim())
          .filter(m => m);
        if (methods.length > 0) setPaymentMethods(methods);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  // Fetch credit balance whenever customer changes (and only for new invoices)
  useEffect(() => {
    if (!formData.customer_id || invoice) {
      setCreditBalance(null);
      setApplyCredit(false);
      setCreditApplied(0);
      return;
    }
    let cancelled = false;
    api.getCustomerCreditBalance(formData.customer_id)
      .then(res => {
        if (!cancelled) setCreditBalance(res.data);
      })
      .catch(() => { if (!cancelled) setCreditBalance(null); });
    return () => { cancelled = true; };
  }, [formData.customer_id, invoice]);

  const subtotal = useMemo(() => {
    return formData.items.reduce((sum, item) => sum + (item.qty * item.price), 0);
  }, [formData.items]);

  const total = subtotal - (formData.discount || 0);
  const outstandingCredit = creditBalance?.outstanding || 0;
  const remainingToPay = Math.max(0, total - (formData.paid_amount || 0));

  // Auto-recompute credit_applied when toggled or total changes
  useEffect(() => {
    if (applyCredit && outstandingCredit > 0) {
      setCreditApplied(Math.min(outstandingCredit, remainingToPay));
    } else {
      setCreditApplied(0);
    }
  }, [applyCredit, outstandingCredit, total, formData.paid_amount]);

  const balance = Math.max(0, total - (formData.paid_amount || 0) - creditApplied);

  const updateItem = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    
    // If product changed, update product name and price
    if (field === 'product_id') {
      const product = products.find(p => p.id === value);
      if (product) {
        newItems[index].product_name = product.name;
        newItems[index].price = product.price;
        newItems[index].serial = ''; // Reset serial
      }
    }
    
    setFormData({ ...formData, items: newItems });
  };

  const addItem = () => {
    setFormData({ ...formData, items: [...formData.items, emptyItem()] });
  };

  // ProductPicker → set product on a specific row
  const setRowFromProduct = (index, product, matchedSerial = null) => {
    const newItems = [...formData.items];
    newItems[index] = {
      ...newItems[index],
      product_id: product.id,
      product_name: product.name,
      price: product.price || newItems[index].price || 0,
      serial: matchedSerial?.sn || ''
    };
    setFormData({ ...formData, items: newItems });
  };

  // Quick-add: scan or paste a serial → server returns line item details → append/replace empty line
  const handleQuickAddBySerial = async () => {
    const sn = quickSerial.trim();
    if (!sn) return;
    setQuickAddMsg('');
    // Already on this invoice?
    if (formData.items.some(i => i.serial === sn)) {
      setQuickAddMsg(`Serial ${sn} is already on this invoice`);
      return;
    }
    try {
      const res = await api.quickAddBySerial(sn);
      const li = res.data;
      const newItem = {
        product_id: li.product_id,
        product_name: li.product_name,
        serial: li.serial,
        qty: li.qty || 1,
        price: li.price || 0
      };
      // Replace the first fully-empty row, else append
      const items = [...formData.items];
      const emptyIdx = items.findIndex(i => !i.product_id && !i.serial);
      if (emptyIdx >= 0) {
        items[emptyIdx] = newItem;
      } else {
        items.push(newItem);
      }
      setFormData({ ...formData, items });
      setQuickAddMsg(`✓ Added ${li.product_name} (SN: ${li.serial})`);
      setQuickSerial('');
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Serial not found or not available';
      setQuickAddMsg(detail);
    }
  };

  const removeItem = (index) => {
    if (formData.items.length === 1) return;
    setFormData({ ...formData, items: formData.items.filter((_, i) => i !== index) });
  };

  // Open the multi-serial picker for a given row.
  const openBulkSerials = (index) => {
    const row = formData.items[index];
    const product = products.find(p => p.id === row.product_id);
    if (!product) {
      alert('Pick a product on this row first.');
      return;
    }
    setBulkSerialState({
      rowIndex: index,
      product,
      defaultQty: Math.max(1, parseInt(row.qty) || 1),
    });
  };

  // Confirm callback from SerialSelectorModal.
  // Replaces the originating row with N qty-1 rows, one per selected serial.
  const handleBulkSerialConfirm = (selectedSerials) => {
    if (!bulkSerialState) return;
    const { rowIndex, product } = bulkSerialState;
    const baseRow = formData.items[rowIndex];
    const expanded = selectedSerials.map((sn) => ({
      product_id: product.id,
      product_name: product.name,
      serial: sn,
      qty: 1,
      price: baseRow.price || product.price || 0,
    }));
    const nextItems = [
      ...formData.items.slice(0, rowIndex),
      ...expanded,
      ...formData.items.slice(rowIndex + 1),
    ];
    setFormData({ ...formData, items: nextItems });
    setBulkSerialState(null);
  };

  const getAvailableSerials = (productId, currentSerial = '') => {
    const product = products.find(p => p.id === productId);
    if (!product) return [];
    
    // Get in-stock serials + the currently selected serial (if any)
    const usedSerials = formData.items
      .filter(item => item.product_id === productId && item.serial && item.serial !== currentSerial)
      .map(item => item.serial);
    
    return (product.serials || []).filter(s => 
      (s.status === 'In Stock' || s.sn === currentSerial) && !usedSerials.includes(s.sn)
    );
  };

  const handleCreateCustomer = async () => {
    if (!newCustomer.name || !newCustomer.phone) {
      alert('Customer name and phone are required');
      return;
    }
    try {
      const res = await api.createCustomer(newCustomer);
      setCustomers([...customers, res.data]);
      setFormData({ ...formData, customer_id: res.data.id });
      setShowNewCustomer(false);
      setNewCustomer({ name: '', phone: '', email: '' });
    } catch (error) {
      alert('Failed to create customer');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.customer_id) {
      alert('Please select a customer');
      return;
    }
    
    if (formData.items.some(i => !i.product_id)) {
      alert('Please select a product for each line item');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        total: total,
        credit_applied: invoice ? 0 : creditApplied  // Only apply credit on new invoices
      };
      
      let result;
      if (invoice) {
        result = await api.updateInvoice(invoice.id, payload);
      } else {
        result = await api.createInvoice(payload);
      }
      
      onSaved && onSaved(result.data);
      onClose();
    } catch (error) {
      console.error(error);
      alert('Failed to save invoice');
    } finally {
      setLoading(false);
    }
  };

  const filteredCustomers = customers.filter(c =>
    c.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    c.phone.includes(customerSearch)
  );

  const selectedCustomer = customers.find(c => c.id === formData.customer_id);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={invoice ? 'Edit Invoice' : 'Create New Invoice'}
      size="xl"
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer Selection */}
        <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Customer</h3>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setShowNewCustomer(!showNewCustomer)}
              data-testid="toggle-new-customer"
            >
              {showNewCustomer ? 'Select Existing' : '+ New Customer'}
            </Button>
          </div>

          {showNewCustomer ? (
            <div className="grid grid-cols-3 gap-3">
              <Input
                placeholder="Customer Name *"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
                data-testid="new-customer-name"
              />
              <Input
                placeholder="Phone *"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
                data-testid="new-customer-phone"
              />
              <div className="flex gap-2">
                <Input
                  placeholder="Email"
                  value={newCustomer.email}
                  onChange={(e) => setNewCustomer({ ...newCustomer, email: e.target.value })}
                  data-testid="new-customer-email"
                />
                <Button type="button" size="sm" onClick={handleCreateCustomer} data-testid="save-new-customer">
                  Add
                </Button>
              </div>
            </div>
          ) : (
            <>
              <div className="relative mb-2">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />
                <Input
                  placeholder="Search customers..."
                  value={customerSearch}
                  onChange={(e) => setCustomerSearch(e.target.value)}
                  className="pl-9"
                  data-testid="customer-search"
                />
              </div>
              <Select
                value={formData.customer_id}
                onChange={(e) => setFormData({ ...formData, customer_id: e.target.value })}
                data-testid="customer-select"
              >
                <option value="">-- Select Customer --</option>
                {filteredCustomers.map(c => (
                  <option key={c.id} value={c.id}>
                    {`${c.name} (${c.phone})`}
                  </option>
                ))}
              </Select>
              {selectedCustomer && (
                <div className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                  📧 {selectedCustomer.email || 'No email'} | 📍 {selectedCustomer.address || 'No address'}
                </div>
              )}
            </>
          )}
        </div>

        {/* Line Items */}
        <div>
          <div className="flex justify-between items-center mb-3">
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Line Items</h3>
            <Button type="button" size="sm" onClick={addItem} data-testid="add-item-button">
              <Plus size={14} className="mr-1" />
              Add Item
            </Button>
          </div>

          {/* Quick-Add by Serial (barcode scan or type) */}
          <div className="mb-3 p-3 bg-cyan-50 dark:bg-cyan-900/20 border border-cyan-200 dark:border-cyan-800 rounded-md">
            <label className="text-xs font-semibold text-cyan-900 dark:text-cyan-200 mb-1 flex items-center gap-1">
              <Barcode size={14} /> Scan or paste a serial number to add an item instantly
            </label>
            <div className="flex gap-2 mt-1">
              <Input
                value={quickSerial}
                onChange={(e) => setQuickSerial(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleQuickAddBySerial(); } }}
                placeholder="e.g. MSI4060-123456"
                data-testid="quick-add-serial-input"
                className="flex-1"
              />
              <Button type="button" size="sm" onClick={handleQuickAddBySerial} data-testid="quick-add-serial-btn">
                Add
              </Button>
            </div>
            {quickAddMsg && (
              <p className={`text-xs mt-2 ${quickAddMsg.startsWith('✓') ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600 dark:text-red-400'}`} data-testid="quick-add-message">
                {quickAddMsg}
              </p>
            )}
          </div>

          <div className="space-y-3">
            {formData.items.map((item, index) => {
              const availableSerials = getAvailableSerials(item.product_id, item.serial);
              return (
                <div
                  key={index}
                  className="grid grid-cols-12 gap-2 items-start p-3 bg-gray-50 dark:bg-gray-900 rounded-lg"
                  data-testid={`invoice-item-${index}`}
                >
                  <div className="col-span-4">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Product</label>
                    {item.product_id ? (
                      <div className="flex items-center justify-between px-3 py-2 text-sm bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-md">
                        <span className="font-medium truncate">{item.product_name}</span>
                        <button type="button" onClick={() => updateItem(index, 'product_id', '')} className="text-xs text-blue-600 hover:underline ml-2" data-testid={`item-change-product-${index}`}>
                          Change
                        </button>
                      </div>
                    ) : (
                      <ProductPicker
                        onSelect={(p, ms) => setRowFromProduct(index, p, ms)}
                        placeholder="Search name, brand, SKU, serial..."
                        testId={`item-product-picker-${index}`}
                      />
                    )}
                  </div>

                  <div className="col-span-3">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Serial (optional)</label>
                    <div className="flex gap-1">
                      <Select
                        value={item.serial}
                        onChange={(e) => updateItem(index, 'serial', e.target.value)}
                        disabled={!item.product_id}
                        data-testid={`item-serial-${index}`}
                        className="flex-1"
                      >
                        <option value="">No Serial</option>
                        {availableSerials.map(s => (
                          <option key={s.sn} value={s.sn}>{s.sn}</option>
                        ))}
                      </Select>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => openBulkSerials(index)}
                        disabled={!item.product_id}
                        title="Select multiple serials"
                        data-testid={`item-bulk-serials-${index}`}
                      >
                        <ListChecks size={14} />
                      </Button>
                    </div>
                  </div>

                  <div className="col-span-1">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Qty</label>
                    <Input
                      type="number"
                      min="1"
                      value={item.qty}
                      onChange={(e) => updateItem(index, 'qty', parseInt(e.target.value) || 1)}
                      data-testid={`item-qty-${index}`}
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Price</label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={item.price}
                      onChange={(e) => updateItem(index, 'price', parseFloat(e.target.value) || 0)}
                      data-testid={`item-price-${index}`}
                    />
                  </div>

                  <div className="col-span-1">
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Total</label>
                    <div className="px-2 py-2 text-sm font-medium text-gray-900 dark:text-white">
                      {formatCurrency(item.qty * item.price, currency)}
                    </div>
                  </div>

                  <div className="col-span-1 flex items-end justify-center pt-5">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => removeItem(index)}
                      disabled={formData.items.length === 1}
                      data-testid={`remove-item-${index}`}
                    >
                      <Trash size={16} className="text-red-600" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Summary Section */}
        <div className="grid grid-cols-2 gap-6">
          <div className="space-y-4">
            <Select
              label="Payment Type"
              value={formData.payment_type}
              onChange={(e) => {
                const pt = e.target.value;
                const next = { ...formData, payment_type: pt };
                if (pt === 'Credit') {
                  const cust = customers.find(c => c.id === formData.customer_id);
                  if (cust?.default_credit_days && !formData.credit_days) {
                    next.credit_days = cust.default_credit_days;
                  } else if (!formData.credit_days) {
                    next.credit_days = 30;
                  }
                } else {
                  next.credit_days = 0;
                  next.due_date = '';
                }
                next.payment_method = pt;
                setFormData(next);
              }}
              data-testid="payment-type"
            >
              {paymentMethods.map(m => (
                <option key={m} value={m}>{m}</option>
              ))}
              <option value="Credit">Credit (Dealer)</option>
            </Select>

            {formData.payment_type === 'Credit' && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Credit Period</label>
                  <div className="grid grid-cols-5 gap-1">
                    {[7, 14, 21, 30].map((d) => (
                      <Button
                        key={d}
                        type="button"
                        size="sm"
                        variant={parseInt(formData.credit_days) === d ? 'primary' : 'outline'}
                        onClick={() => setFormData({ ...formData, credit_days: d })}
                        data-testid={`credit-days-${d}`}
                      >
                        {d}d
                      </Button>
                    ))}
                    <Input
                      type="number"
                      min={1}
                      value={[7, 14, 21, 30].includes(parseInt(formData.credit_days)) ? '' : (formData.credit_days || '')}
                      onChange={(e) => setFormData({ ...formData, credit_days: parseInt(e.target.value) || 0 })}
                      placeholder="Custom"
                      data-testid="credit-days-custom"
                    />
                  </div>
                </div>
                <div className="text-xs text-amber-900 dark:text-amber-200" data-testid="due-date-preview">
                  <strong>Due Date:</strong> {(() => {
                    const days = parseInt(formData.credit_days) || 0;
                    if (days <= 0) return '—';
                    const d = new Date();
                    d.setDate(d.getDate() + days);
                    return d.toISOString().slice(0, 10);
                  })()}
                </div>
                {(() => {
                  const cust = customers.find(c => c.id === formData.customer_id);
                  if (!cust?.is_dealer) {
                    return <p className="text-xs text-orange-600">⚠ This customer is not marked as a Dealer. Credit sales are usually for dealers — go to Customers to enable Dealer status &amp; set a credit limit.</p>;
                  }
                  if (cust.credit_limit > 0) {
                    return <p className="text-xs text-amber-800 dark:text-amber-300">Dealer credit limit: <strong>{formatCurrency(cust.credit_limit, currency)}</strong></p>;
                  }
                  return null;
                })()}
              </div>
            )}

            <Select
              label="Status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              data-testid="invoice-status"
            >
              {INVOICE_STATUSES.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </Select>

            <Textarea
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
              data-testid="invoice-notes"
            />
          </div>

          <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg space-y-3">
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
              <span className="font-semibold text-gray-900 dark:text-white" data-testid="subtotal">
                {formatCurrency(subtotal, currency)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <label className="text-sm text-gray-600 dark:text-gray-400">Discount:</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.discount}
                onChange={(e) => setFormData({ ...formData, discount: parseFloat(e.target.value) || 0 })}
                className="w-32 text-right"
                data-testid="discount-input"
              />
            </div>

            <div className="flex justify-between items-center pt-3 border-t border-gray-300 dark:border-gray-700">
              <span className="text-lg font-bold text-gray-900 dark:text-white">Total:</span>
              <span className="text-lg font-bold text-blue-600 dark:text-blue-400" data-testid="total">
                {formatCurrency(total, currency)}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <label className="text-sm text-gray-600 dark:text-gray-400">Paid Amount:</label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={formData.paid_amount}
                onChange={(e) => setFormData({ ...formData, paid_amount: parseFloat(e.target.value) || 0 })}
                className="w-32 text-right"
                data-testid="paid-amount"
              />
            </div>

            {/* Customer credit application */}
            {!invoice && outstandingCredit > 0 && (
              <div className="rounded-md border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-3 space-y-2" data-testid="credit-apply-panel">
                <div className="flex items-center gap-2 text-sm text-emerald-900 dark:text-emerald-200">
                  <Sparkles size={14} />
                  <span>This customer has <strong>{formatCurrency(outstandingCredit, currency)}</strong> available credit.</span>
                </div>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={applyCredit}
                    onChange={(e) => setApplyCredit(e.target.checked)}
                    className="w-4 h-4"
                    data-testid="apply-credit-toggle"
                  />
                  <span className="text-sm text-emerald-900 dark:text-emerald-200">Apply credit to this invoice</span>
                </label>
                {applyCredit && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-emerald-700 dark:text-emerald-300">Credit Applied:</span>
                    <span className="font-semibold text-emerald-700 dark:text-emerald-300" data-testid="credit-applied-amount">
                      -{formatCurrency(creditApplied, currency)}
                    </span>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-between items-center pt-2 border-t border-gray-300 dark:border-gray-700">
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Balance:</span>
              <Badge variant={balance > 0 ? 'warning' : balance < 0 ? 'info' : 'success'}>
                {formatCurrency(balance, currency)}
              </Badge>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button type="button" variant="secondary" onClick={onClose} data-testid="cancel-invoice">
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={loading} data-testid="save-invoice">
            {loading ? 'Saving...' : (invoice ? 'Update Invoice' : 'Create Invoice')}
          </Button>
        </div>
      </form>

      {bulkSerialState && (
        <SerialSelectorModal
          isOpen={!!bulkSerialState}
          onClose={() => setBulkSerialState(null)}
          product={bulkSerialState.product}
          excludedSerials={formData.items
            .map((it, i) => (i === bulkSerialState.rowIndex ? null : (it.product_id === bulkSerialState.product.id ? it.serial : null)))
            .filter(Boolean)}
          defaultQuantity={bulkSerialState.defaultQty}
          onConfirm={handleBulkSerialConfirm}
        />
      )}
    </Modal>
  );
};
